import { useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import BillComponent from "./BillComponent";
import useDetectPrint from "../../hooks/useDetectPrint";
import { isMealBillAndroid, androidPrinter } from "../../native/mealbill-android.mjs";

/**
 * Build a flat ESC/POS receipt object from the KOT printData for the Android
 * native thermal printer bridge. Only the last KOT's items are printed (same
 * logic as BillComponent's ProductTable).
 *
 * jobId format: "kot:<branchOrderNumber>:<kotIndex>" — stable per print event.
 */
function createAndroidKOTReceipt({ printData, restaurant, branchAddress }) {
  const lastKot = printData?.KOTS?.[printData.KOTS.length - 1];
  const kotIndex = printData?.KOTS?.length ?? 1;
  const branchOrderNumber = (printData?.branchOrderNumber ?? "").replace(/\s/g, "");
  const jobId = `kot:${branchOrderNumber}:${kotIndex}`.replace(/[^A-Za-z0-9._:-]/g, "-").slice(0, 128);

  const lines = [];

  // Header
  if (restaurant) lines.push({ text: restaurant, align: "center", bold: true, size: 28 });
  if (branchAddress) lines.push({ text: branchAddress, align: "center" });
  lines.push({ text: "--------------------------------", align: "center" });

  // Order meta
  const orderType = printData?.orderType ?? "";
  const tableNumber = printData?.tableNumber;
  const kotNumber = lastKot?.kotNumber;
  const customerName = printData?.customerName;
  const customerMobile = printData?.customerMobile;
  const remarks = printData?.remarks;
  const orderDate = printData?.orderDate ?? new Date().toLocaleString("en-IN");

  if (orderType) lines.push({ text: `Type: ${orderType}${tableNumber ? `   Table: ${tableNumber}` : ""}` });
  if (kotNumber) lines.push({ text: `Token: ${kotNumber}`, bold: true });
  if (customerName) lines.push({ text: `Name: ${customerName}${customerMobile ? ` (${customerMobile})` : ""}` });
  lines.push({ text: orderDate });
  lines.push({ text: "--------------------------------", align: "center" });

  // Column header
  lines.push({ columns: ["Item", "Qty"], bold: true });
  lines.push({ text: "--------------------------------", align: "center" });

  // Items
  let totalQty = 0;
  (lastKot?.orderItems ?? []).forEach((item) => {
    const qty = item.quantity ?? 0;
    totalQty += qty;
    lines.push({ columns: [item.itemName ?? "", String(qty)] });
    // Toppings
    if (item?.activeTopping?.length > 0) {
      const toppingNames = item.activeTopping.map((t) => t.itemName).join(", ");
      lines.push({ text: `  + ${toppingNames}` });
    }
  });

  lines.push({ text: "--------------------------------", align: "center" });
  lines.push({ columns: ["TOTAL QTY", String(totalQty)], bold: true });

  if (remarks) {
    lines.push({ text: "--------------------------------", align: "center" });
    lines.push({ text: `Remarks: ${remarks}` });
  }

  return { jobId, printer: "kitchen", lines };
}

const PrintComponent = ({
  headers,
  footers,
  printData,
  restaurant,
  logo,
  gstNumber,
  containerStyle,
  receiptMessage,
  branchAddress,
}) => {
  const isPrinting = useDetectPrint();

  const componentRef = useRef();
  const androidJobs = useRef(new Set());
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      // alert("print completed");
    },
  });

  useEffect(() => {
    if (isPrinting || !printData) return;

    // ── Android native thermal print path ──────────────────────────────────
    if (isMealBillAndroid()) {
      try {
        const receipt = createAndroidKOTReceipt({ printData, restaurant, branchAddress });
        if (androidJobs.current.has(receipt.jobId)) return; // deduplicate within this mount
        androidJobs.current.add(receipt.jobId);
        androidPrinter.print(receipt).then(
          (result) => console.log("Android KOT print:", result.status, receipt.jobId),
          (error) => {
            console.error("Android KOT print failed:", error.message, error.result);
            window.alert(`Print failed: ${error.message}`);
          }
        );
      } catch (error) {
        console.error("Android KOT receipt build error:", error.message);
        window.alert(`Print error: ${error.message}`);
      }
      return; // Never fall back to browser print after native attempt
    }

    // ── Electron desktop path ───────────────────────────────────────────────
    console.log("isElectron", window.api);
    if (window?.api?.isElectron) {
      console.log("isElectron inside", window.api);
      window.api.send(printData);
    } else {
      // ── Browser fallback (print dialog) ──────────────────────────────────
      handlePrint();
    }
  }, [printData, isPrinting]);

  return (
    <div style={containerStyle}>
      <div style={{ display: "none" }}>
        <BillComponent
          ref={componentRef}
          orderData={printData}
          restaurant={restaurant}
          logo={logo}
          branchAddress={branchAddress}
        />
      </div>
    </div>
  );
};
export default PrintComponent;
